import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, MapPin, Star, Navigation, Map, Crosshair } from 'lucide-react';
import axios from 'axios';
import { Loader } from '@googlemaps/js-api-loader';
import SimpleMap from './SimpleMap';

const LocationSearch = ({ 
  onLocationSelect, 
  placeholder = "Search for a location...",
  showNearbyPlaces = true,
  showMap = true,
  className = "" 
}) => {
  const [query, setQuery] = useState('');
  const [places, setPlaces] = useState([]);
  const [nearbyPlaces, setNearbyPlaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [map, setMap] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [showMapView, setShowMapView] = useState(false);
  const [googleApi, setGoogleApi] = useState(null);
  const [useSimpleMap, setUseSimpleMap] = useState(false);
  const searchTimeoutRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);

  // Initialize Google Maps
  useEffect(() => {
    if (showMap && !googleApi && !useSimpleMap) {
      initializeMap();
    }
  }, [showMap, googleApi, useSimpleMap]);

  const initializeMap = async () => {
    setMapLoading(true);
    try {
      const loader = new Loader({
        apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'your-api-key-here',
        version: 'weekly',
        libraries: ['places']
      });

      const google = await loader.load();
      setGoogleApi(google);
      
      // Create map instance
      const mapInstance = new google.maps.Map(mapRef.current, {
        center: { lat: 19.0760, lng: 72.8777 }, // Default to Mumbai
        zoom: 12,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ]
      });

      setMap(mapInstance);
    } catch (error) {
      console.error('Failed to load Google Maps:', error);
      // Fallback to simple map
      setUseSimpleMap(true);
    } finally {
      setMapLoading(false);
    }
  };

  useEffect(() => {
    // Get user's current location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(location);
          
          // Update map if available
          if (map && googleApi) {
            map.setCenter(location);
            map.setZoom(15);
            
            // Add user location marker
            new googleApi.maps.Marker({
              position: location,
              map: map,
              title: 'Your Location',
              icon: {
                url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="8" fill="#4285F4" stroke="white" stroke-width="2"/>
                    <circle cx="12" cy="12" r="3" fill="white"/>
                  </svg>
                `),
                scaledSize: new googleApi.maps.Size(24, 24),
                anchor: new googleApi.maps.Point(12, 12)
              }
            });
          }
        },
        (error) => {
          console.log('Location access denied:', error);
        }
      );
    }
  }, [map, googleApi]);

  const handlePlaceSelect = useCallback((place) => {
    onLocationSelect(place);
    setQuery(place.name);
    setShowResults(false);
    
    // Center map on selected place
    if (map && place.location) {
      map.setCenter({ lat: place.location.lat, lng: place.location.lng });
      map.setZoom(16);
    }
  }, [onLocationSelect, map]);

  const addMarkersToMap = useCallback((places, type) => {
    if (!googleApi) return;
    
    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    places.forEach((place, index) => {
      if (place.location) {
        const marker = new googleApi.maps.Marker({
          position: { lat: place.location.lat, lng: place.location.lng },
          map: map,
          title: place.name,
          icon: {
            url: type === 'search' 
              ? 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 0C6.13 0 3 3.13 3 7c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#FF6B6B"/>
                    <circle cx="10" cy="7" r="3" fill="white"/>
                  </svg>
                `)
              : 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 0C5.79 0 4 1.79 4 4c0 4.2 4 10.4 4 10.4s4-6.2 4-10.4c0-2.21-1.79-4-4-4z" fill="#4CAF50"/>
                    <circle cx="8" cy="4" r="2" fill="white"/>
                  </svg>
                `),
            scaledSize: new googleApi.maps.Size(type === 'search' ? 20 : 16, type === 'search' ? 20 : 16),
            anchor: new googleApi.maps.Point(type === 'search' ? 10 : 8, type === 'search' ? 20 : 16)
          }
        });

        // Add click listener
        marker.addListener('click', () => {
          handlePlaceSelect(place);
        });

        // Add info window
        const infoWindow = new googleApi.maps.InfoWindow({
          content: `
            <div style="padding: 8px; max-width: 200px;">
              <div style="font-weight: bold; margin-bottom: 4px;">${place.name}</div>
              <div style="font-size: 12px; color: #666;">${place.address}</div>
              ${place.rating ? `<div style="font-size: 12px; color: #f39c12; margin-top: 4px;">⭐ ${place.rating}</div>` : ''}
            </div>
          `
        });

        marker.addListener('click', () => {
          infoWindow.open(map, marker);
        });

        markersRef.current.push(marker);
      }
    });
  }, [googleApi, map, handlePlaceSelect]);

  const searchPlaces = useCallback(async () => {
    if (!query.trim()) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({ q: query });
      if (userLocation) {
        params.append('lat', userLocation.lat);
        params.append('lng', userLocation.lng);
      }

      const response = await axios.get(`/api/google/places/search?${params}`);
      setPlaces(response.data.places);
      setShowResults(true);
      
      // Add markers to map
      if (map && googleApi && response.data.places.length > 0) {
        addMarkersToMap(response.data.places, 'search');
      }
    } catch (error) {
      console.error('Search places error:', error);
      setPlaces([]);
    } finally {
      setLoading(false);
    }
  }, [query, userLocation, map, googleApi, addMarkersToMap]);

  const loadNearbyPlaces = useCallback(async () => {
    if (!userLocation) return;
    
    try {
      const response = await axios.get(`/api/google/places/nearby?lat=${userLocation.lat}&lng=${userLocation.lng}&radius=5000`);
      setNearbyPlaces(response.data.places.slice(0, 5));
      
      // Add markers to map
      if (map && googleApi && response.data.places.length > 0) {
        addMarkersToMap(response.data.places.slice(0, 5), 'nearby');
      }
    } catch (error) {
      console.error('Load nearby places error:', error);
    }
  }, [userLocation, map, googleApi, addMarkersToMap]);

  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for search
    if (query.length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        searchPlaces();
      }, 500);
    } else {
      setPlaces([]);
      setShowResults(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [query, searchPlaces]);

  useEffect(() => {
    // Load nearby places when user location is available
    if (userLocation && showNearbyPlaces) {
      loadNearbyPlaces();
    }
  }, [userLocation, showNearbyPlaces, loadNearbyPlaces]);

  const handleInputFocus = () => {
    if (places.length > 0 || nearbyPlaces.length > 0) {
      setShowResults(true);
    }
  };

  const handleInputBlur = () => {
    // Delay hiding results to allow for clicks
    setTimeout(() => setShowResults(false), 200);
  };

  const getPlaceIcon = (types) => {
    if (!types || types.length === 0) return <MapPin className="w-4 h-4" />;
    
    if (types.includes('transit_station')) return <Navigation className="w-4 h-4" />;
    if (types.includes('establishment')) return <MapPin className="w-4 h-4" />;
    if (types.includes('restaurant')) return <MapPin className="w-4 h-4" />;
    
    return <MapPin className="w-4 h-4" />;
  };

  const formatDistance = (distance) => {
    if (distance < 1) return `${Math.round(distance * 1000)}m`;
    return `${distance.toFixed(1)}km`;
  };

  const toggleMapView = () => {
    setShowMapView(!showMapView);
  };

  const centerOnUserLocation = () => {
    if (userLocation && map) {
      map.setCenter(userLocation);
      map.setZoom(15);
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Search Input with Map Toggle */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          className="w-full pl-10 pr-20 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {loading && (
          <div className="absolute right-12 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
          </div>
        )}
        {showMap && (
          <button
            onClick={toggleMapView}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-blue-500 transition-colors"
            title="Toggle Map View"
          >
            <Map className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Map View */}
      {showMap && showMapView && (
        <div className="mt-4">
          {useSimpleMap ? (
            <SimpleMap
              userLocation={userLocation}
              places={[...places, ...nearbyPlaces]}
              onPlaceSelect={handlePlaceSelect}
            />
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
              <div className="p-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Map className="w-5 h-5 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">Location Map</span>
                </div>
                <div className="flex items-center space-x-2">
                  {userLocation && (
                    <button
                      onClick={centerOnUserLocation}
                      className="p-1 text-gray-500 hover:text-blue-500 transition-colors"
                      title="Center on my location"
                    >
                      <Crosshair className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              <div className="relative h-64">
                {mapLoading ? (
                  <div className="flex items-center justify-center h-full bg-gray-100">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                      <p className="text-sm text-gray-600">Loading map...</p>
                    </div>
                  </div>
                ) : (
                  <div ref={mapRef} className="w-full h-full" />
                )}
              </div>
              {userLocation && (
                <div className="p-3 bg-gray-50 text-xs text-gray-600">
                  📍 Your location: {userLocation.lat.toFixed(6)}, {userLocation.lng.toFixed(6)}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Search Results */}
      {showResults && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
          {/* Search Results */}
          {places.length > 0 && (
            <div className="p-2">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide px-2 py-1">
                Search Results
              </div>
              {places.map((place, index) => (
                <button
                  key={`${place.id}-${index}`}
                  onClick={() => handlePlaceSelect(place)}
                  className="w-full text-left p-3 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 text-gray-400">
                      {getPlaceIcon(place.types)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">
                        {place.name}
                      </div>
                      <div className="text-sm text-gray-500 truncate">
                        {place.address}
                      </div>
                      {place.rating && (
                        <div className="flex items-center space-x-1 mt-1">
                          <Star className="w-3 h-3 text-yellow-400 fill-current" />
                          <span className="text-xs text-gray-600">
                            {place.rating} ({place.userRatingsTotal || 0})
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Nearby Places */}
          {showNearbyPlaces && nearbyPlaces.length > 0 && (
            <div className="p-2 border-t border-gray-100">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide px-2 py-1">
                Nearby Places
              </div>
              {nearbyPlaces.map((place, index) => (
                <button
                  key={`nearby-${place.id}-${index}`}
                  onClick={() => handlePlaceSelect(place)}
                  className="w-full text-left p-3 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 text-gray-400">
                      {getPlaceIcon(place.types)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">
                        {place.name}
                      </div>
                      <div className="text-sm text-gray-500 truncate">
                        {place.address}
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        {place.rating && (
                          <div className="flex items-center space-x-1">
                            <Star className="w-3 h-3 text-yellow-400 fill-current" />
                            <span className="text-xs text-gray-600">
                              {place.rating}
                            </span>
                          </div>
                        )}
                        {place.distance && (
                          <span className="text-xs text-gray-500">
                            {formatDistance(place.distance)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* No Results */}
          {places.length === 0 && nearbyPlaces.length === 0 && query.length >= 2 && !loading && (
            <div className="p-4 text-center text-gray-500">
              <MapPin className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p>No places found</p>
              <p className="text-sm">Try a different search term</p>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="p-4 text-center text-gray-500">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto mb-2"></div>
              <p>Searching...</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LocationSearch; 